import { MenuItem, TextField } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { isInvestmentCountryCode } from '@inversor-hub/types';
import AddressFields from '@/components/ui/AddressFields.tsx';
import { investmentCountryOptions } from '@/features/investments/countries.ts';
import FormSection from '@/features/investments/form/components/FormSection.tsx';
import { InvestmentFormData } from '@/features/investments/form/investment-form.schema.ts';

const IdentitySection = () => {
  const {
    control,
    formState: { errors },
    setValue,
    trigger,
  } = useFormContext<InvestmentFormData>();

  const [country, address, city, postalCode, province] = useWatch({
    control,
    name: ['country', 'address', 'city', 'postalCode', 'province'],
  });

  return (
    <FormSection title="1. Identidad" description="Datos básicos para identificar la inversión.">
      <Controller
        name="alias"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="Alias"
            fullWidth
            error={!!errors.alias}
            helperText={errors.alias?.message}
          />
        )}
      />

      <Controller
        name="identificationCode"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="Código de identificación"
            fullWidth
            error={!!errors.identificationCode}
            helperText={
              errors.identificationCode?.message ??
              'Opcional. Usa un código corto y estable para que el inquilino lo indique en el concepto del pago.'
            }
            inputProps={{
              maxLength: 40,
            }}
          />
        )}
      />

      <Controller
        name="country"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            select
            label="País"
            fullWidth
            error={!!errors.country}
            helperText={errors.country?.message}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: { maxHeight: 320 },
                },
              },
            }}
          >
            {investmentCountryOptions.map((option) => (
              <MenuItem key={option.code} value={option.code}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      <AddressFields
        formData={{
          country,
          address,
          city,
          postalCode,
          province,
        }}
        onChange={(nextValue) => {
          setValue('address', nextValue.address, { shouldDirty: true, shouldValidate: true });
          if (nextValue.country && isInvestmentCountryCode(nextValue.country)) {
            setValue('country', nextValue.country, { shouldDirty: true, shouldValidate: true });
          }
          setValue('city', nextValue.city, { shouldDirty: true, shouldValidate: true });
          setValue('postalCode', nextValue.postalCode, { shouldDirty: true, shouldValidate: true });
          setValue('province', nextValue.province, { shouldDirty: true, shouldValidate: true });
        }}
        onFieldBlur={(field) => {
          void trigger(field);
        }}
        postalCodeRequired={false}
        errorFields={{
          address: !!errors.address,
          city: !!errors.city,
          postalCode: !!errors.postalCode,
          province: !!errors.province,
        }}
        helperTexts={{
          address: errors.address?.message,
          city: errors.city?.message,
          postalCode: errors.postalCode?.message ?? 'Opcional si no está disponible',
          province: errors.province?.message,
        }}
      />
    </FormSection>
  );
};

export default IdentitySection;
